import { Icon } from '@ur-solutions/react-components'
import { H2 } from './heading'
import { IconTooltip } from './icon-tooltip'

export interface MonthSummaryCard {
  key: string
  label: string
  /** Optional explanatory tooltip rendered next to the card label. */
  tooltip?: React.ReactNode
  /** Pre-formatted display value (e.g. `154:07`). */
  value: string
  /** Optional secondary hint below the value (e.g. `+12:34 next month`). */
  hint?: React.ReactNode
  /** Icon element (FontAwesome `<Icon>`, `<SVGIcon>`, etc.). */
  icon: React.ReactNode
  /** Background color for the icon tile. CSS color literal. */
  iconBg: string
}

interface MonthSummaryCardsProps {
  title: string
  cards: MonthSummaryCard[]
  /** Optional explanatory tooltip rendered as an `info-circle` next to the title. */
  titleTooltip?: React.ReactNode
  /**
   * Render value placeholders instead of the supplied `value` / `hint` so
   * the layout stays visible while the underlying data is fetching.
   */
  loading?: boolean
  /** When true, hide card grid and render only the title row with toggle. */
  collapsed?: boolean
  /** When provided, render a text button in the title that toggles `collapsed`. */
  onToggleCollapsed?: () => void
  /** Label for the toggle button (already localized by caller). */
  toggleLabel?: string
  /**
   * Extra control rendered in the title row (right-aligned, before the
   * collapse button) — e.g. a scope toggle for what the sums include.
   */
  titleExtra?: React.ReactNode
}

interface CardTileProps extends Omit<MonthSummaryCard, 'key'> {
  loading?: boolean
}

const CardTile: React.FC<CardTileProps> = ({
  icon,
  iconBg,
  label,
  tooltip,
  value,
  hint,
  loading,
}) => (
  <div className="grid grid-cols-[auto_1fr] gap-8 p-8 bg-white rounded-xl shadow-card md:gap-6 md:p-6 max-md:gap-4 max-md:p-4">
    <div
      className="flex items-center justify-center w-24 h-24 rounded-[20px] md:w-20 md:h-20 md:rounded-2xl max-md:w-16 max-md:h-16 max-md:rounded-xl [&_svg]:w-[60%] [&_i]:text-5xl [&_i]:font-thin md:[&_i]:text-4xl max-md:[&_i]:text-3xl"
      style={{ background: iconBg }}
    >
      {icon}
    </div>
    <div className="flex flex-col justify-center font-semibold">
      <span className="inline-flex items-center gap-2 text-gray-500">
        {label}
        {tooltip && (
          <IconTooltip
            triggerClassName="inline-flex text-gray-400 hover:text-gray-600"
            contentClassName="font-medium"
            side="top"
          >
            {tooltip}
          </IconTooltip>
        )}
      </span>
      {loading ? (
        <span
          aria-busy
          className="mt-1 h-8 w-20 rounded bg-gray-200 animate-pulse"
        />
      ) : (
        <span className="text-3xl">
          {value}
          {hint && (
            <p className="text-base text-gray-500 m-0 font-semibold">{hint}</p>
          )}
        </span>
      )}
    </div>
  </div>
)

/**
 * Shared "Sum for [period]" card grid used by the activities and salary
 * views. Pure presentation — callers supply pre-formatted cards plus a
 * title. Layout adapts to screen width (5 / 2 / 1 columns).
 */
export const MonthSummaryCards: React.FC<MonthSummaryCardsProps> = ({
  title,
  cards,
  titleTooltip,
  loading,
  collapsed,
  onToggleCollapsed,
  toggleLabel,
  titleExtra,
}) => (
  <div className="grid grid-cols-5 gap-x-6 gap-y-4 min-[769px]:max-[1720px]:grid-cols-2 max-md:grid-cols-1">
    <H2 className="col-span-full flex items-center gap-2 mt-4 mb-0 font-semibold">
      {title}
      {titleTooltip && (
        <IconTooltip triggerClassName="text-gray-400">
          {titleTooltip}
        </IconTooltip>
      )}
      {titleExtra && <span className="ml-auto">{titleExtra}</span>}
      {onToggleCollapsed && (
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          className={`${titleExtra ? '' : 'ml-auto '}inline-flex items-center gap-2 px-3 py-1 text-base font-medium text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 transition-colors`}
        >
          {toggleLabel}
          <Icon icon={collapsed ? 'chevron-down' : 'chevron-up'} fixedWidth />
        </button>
      )}
    </H2>
    {!collapsed &&
      cards.map(card => {
        const { key, ...rest } = card
        return <CardTile key={key} {...rest} loading={loading} />
      })}
  </div>
)
